/**
 * 设置API服务
 * 处理交易设置和交易商设置的后端API调用
 */

import { apiClient } from './ApiClient';
import { authApi } from './AuthApiService';
import { logger } from '@utils/logger';

export interface TradingSettings {
  maxLeverage: number;
  minOrderSize: number;
  maxOrderSize: number;
  liquidationRatio: number;
  liquidationThreshold?: number;
}

export interface BrokerSettings {
  // 客户交易费率
  makerFeeRate: number;
  takerFeeRate: number;
  // 充值费率和回馈
  depositFeeRate: number;
  depositRebateRate: number;
  rebateTimeWindow: number;
  rebateMaxAmount: number;
  // 对冲设置
  hedgeEnabled: boolean;
  hedgeAutoEnabled: boolean;
  hedgeThreshold: number;
  hedgeRatio: number;
  // 新增字段
  brokerUid?: string;
  referralDepositRewardRate?: number;
}

class SettingsApiService {
  /**
   * 获取交易设置
   */
  async getTradingSettings(): Promise<TradingSettings> {
    logger.info('Fetching trading settings.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<any>(
        '/api/settings/trading',
        { token }
      );

      // ✅ 兼容多种响应格式
      const settings = response?.data?.settings || response;
      
      if (!settings) {
        throw new Error('API 返回的数据格式不正确');
      }
      
      logger.info('Fetched trading settings:', settings);
      return settings;
    } catch (error: any) {
      logger.error('获取交易设置异常:', error);
      throw error;
    }
  }

  /**
   * 更新交易设置
   */
  async updateTradingSettings(data: Partial<TradingSettings>): Promise<TradingSettings> {
    logger.info('Updating trading settings:', data);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.put<{ settings: TradingSettings; message: string }>(
        '/api/settings/trading',
        data,
        { token }
      );
      
      logger.info('Updated trading settings:', response.data?.message);
      return response.data!.settings;
    } catch (error: any) {
      logger.error('更新交易设置异常:', error);
      throw error;
    }
  }

  /**
   * 重置交易设置为默认值
   */
  async resetTradingSettings(): Promise<TradingSettings> {
    logger.info('Resetting trading settings to defaults.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.post<{ settings: TradingSettings; message: string }>(
        '/api/settings/trading/reset',
        {},
        { token }
      );
      
      logger.info('Reset trading settings:', response.data?.message);
      return response.data!.settings;
    } catch (error: any) {
      logger.error('重置交易设置异常:', error);
      throw error;
    }
  }

  /**
   * 获取交易商设置
   */
  async getBrokerSettings(): Promise<BrokerSettings> {
    logger.info('Fetching broker settings.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<any>(
        '/api/settings/broker',
        { token }
      );
      
      // ✅ 兼容多种响应格式
      const settings = response?.data?.settings || response?.data || response;
      
      if (!settings || typeof settings !== 'object') {
        throw new Error('API 返回的数据格式不正确');
      }
      
      logger.info('Fetched broker settings:', settings);
      return settings;
    } catch (error: any) {
      logger.error('获取交易商设置异常:', error);
      throw error;
    }
  }

  /**
   * 更新交易商设置
   */
  async updateBrokerSettings(data: Partial<BrokerSettings>): Promise<BrokerSettings> {
    logger.info('Updating broker settings:', data);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.put<{ settings: BrokerSettings; message: string }>(
        '/api/settings/broker',
        data,
        { token }
      );
      
      // ✅ ApiClient返回 { data: { settings: {...}, message: '...' } }
      if (response.error || !response.data) {
        throw new Error(response.error || '更新交易商设置失败');
      }
      
      logger.info('Updated broker settings:', response.data.message);
      return response.data.settings;
    } catch (error: any) {
      logger.error('更新交易商设置异常:', error);
      throw error;
    }
  }

  /**
   * 重置交易商设置为默认值
   */
  async resetBrokerSettings(): Promise<BrokerSettings> {
    logger.info('Resetting broker settings to defaults.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.post<{ settings: BrokerSettings; message: string }>(
        '/api/settings/broker/reset',
        {},
        { token }
      );
      
      // ✅ ApiClient返回 { data: { settings: {...}, message: '...' } }
      if (response.error || !response.data) {
        throw new Error(response.error || '重置交易商设置失败');
      }
      
      logger.info('Reset broker settings:', response.data.message);
      return response.data.settings;
    } catch (error: any) {
      logger.error('重置交易商设置异常:', error);
      throw error;
    }
  }

  /**
   * ✅ 获取系统设置（充值费率等）- 公开API
   */
  async getSystemSettings(): Promise<any> {
    logger.info('Fetching system settings.');
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<any>(
        '/api/settings/system',  // ✅ 使用公开API
        { token }
      );
      
      // ✅ 兼容多种响应格式
      const settings = response?.data?.settings || response;

      if (!settings || typeof settings !== 'object') {
        throw new Error('API 返回的数据格式不正确');
      }

      logger.info('Fetched system settings:', settings);
      return settings;
    } catch (error: any) {
      logger.error('获取系统设置异常:', error);
      throw error;
    }
  }

  /**
   * ✅ 获取品种的有效费率（包含继承关系：品种 → 交易商 → 系统）
   */
  async getSymbolEffectiveFees(symbol: string): Promise<{
    makerFee: number;
    takerFee: number;
    makerSource: string;
    takerSource: string;
  }> {
    logger.log(`🔍 [SettingsApi] 开始获取 ${symbol} 的有效费率...`);
    try {
      const token = authApi.getToken();
      if (!token) throw new Error('Not authenticated');
      
      const response = await apiClient.get<any>(
        `/api/broker/symbol-fees/${symbol}/effective`,
        { token }
      );
      
      const fees = response?.data || response;
      
      if (!fees) {
        throw new Error('API 返回的费率数据不正确');
      }
      
      logger.log(`✅ [SettingsApi] 获取 ${symbol} 费率成功: Taker=${(fees.takerFee * 100).toFixed(3)}% (来源: ${fees.takerSource})`);
      return fees;
    } catch (error: any) {
      logger.error(`❌ [SettingsApi] 获取 ${symbol} 费率失败:`, error);
      throw error;
    }
  }
}

export const settingsApi = new SettingsApiService();

